import { useEffect, type ReactNode } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRootRouteWithContext, HeadContent, Outlet, Scripts, useRouterState } from "@tanstack/react-router";
import Abertura from "@/components/Abertura";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import NotFound from "@/pages/NotFound";
import { initAnalytics } from "@/lib/analytics";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "@/lib/seo";
import appCss from "@/styles.css?url";

// Dados globais do negócio: entram em todas as páginas. O endereço e os perfis
// precisam bater com o Google Business Profile.
const professionalServiceJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#business`,
  name: "Clara Camille",
  description:
    "Nutricionista brasileira na Austrália. Acompanhamento nutricional e plano alimentar personalizado em português, presencial em Brisbane ou online.",
  url: SITE_URL,
  telephone: "+61423038977",
  email: "nutriclaracamille@gmail.com",
  image: DEFAULT_OG_IMAGE,
  address: {
    "@type": "PostalAddress",
    streetAddress: "8 Harbour Rd",
    addressLocality: "Hamilton",
    addressRegion: "QLD",
    postalCode: "4007",
    addressCountry: "AU",
  },
  hasMap: "https://www.google.com/maps/place/?q=place_id:ChIJP4oYHOVZkWsRVe1_7AfKZT8",
  geo: { "@type": "GeoCoordinates", latitude: -27.4399433, longitude: 153.0671565 },
  // Mesmos horários cadastrados no Google Business Profile.
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Tuesday", "Friday"], opens: "09:00", closes: "18:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Wednesday", "Thursday"], opens: "09:00", closes: "17:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "10:00", closes: "14:00" },
  ],
  areaServed: { "@type": "Country", name: "Australia" },
  availableLanguage: ["Portuguese"],
  priceRange: "Consulta inicial gratuita",
  sameAs: [
    "https://www.instagram.com/nutriclaracamille_/",
    "https://www.tiktok.com/@nutriclaracamilles",
    "https://www.google.com/maps/place/?q=place_id:ChIJP4oYHOVZkWsRVe1_7AfKZT8",
  ],
};

const webSiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: "pt-BR",
  publisher: { "@id": `${SITE_URL}/#business` },
};

const fontPreloads = [
  "/fonts/el-messiri-latin-600-normal.woff2",
  "/fonts/el-messiri-latin-700-normal.woff2",
  "/fonts/inter-latin-400-normal.woff2",
  "/fonts/inter-latin-500-normal.woff2",
  "/fonts/inter-latin-600-normal.woff2",
  "/fonts/inter-latin-700-normal.woff2",
  "/fonts/inter-latin-800-normal.woff2",
].map((href) => ({ rel: "preload", as: "font", type: "font/woff2", href, crossOrigin: "anonymous" as const }));

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      // Valores padrão; cada rota sobrescreve os seus no próprio head().
      { title: "Nutricionista brasileira na Austrália | Clara Camille" },
      {
        name: "description",
        content:
          "Nutricionista brasileira na Austrália. Consulta inicial gratuita e plano alimentar personalizado, presencial em Brisbane ou online.",
      },
      { name: "author", content: "Clara Camille" },
      { name: "theme-color", content: "#6b8f71" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://i.ytimg.com", crossOrigin: "anonymous" },
      ...fontPreloads,
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(professionalServiceJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(webSiteJsonLd) },
    ],
  }),
  shellComponent: RootDocument,
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootDocument({ children }: { children: ReactNode }) {
  // O app e o painel só renderizam no navegador: a abertura vai pronta no HTML para a tela não ficar branca enquanto o
  // JavaScript carrega (src/lib/abertura.ts).
  const abertura = useRouterState({
    select: (s) => {
      const caminho = s.location.pathname;
      if (/^\/app(\/|$)/.test(caminho)) return "app" as const;
      if (/^\/admin(\/|$)/.test(caminho)) return "painel" as const;
      return null;
    },
  });

  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        {abertura && <Abertura onde={abertura} />}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // O GA4 só existe no navegador.
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Outlet />
        <Toaster />
        <Sonner />
        <AnalyticsTracker />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
